// Attach photos from an album to a wish's photo block.
//
// W39.54. `POST /wishes/{wish_id}/blocks/{block_id}/photos`,
// `DELETE .../photos/{album_item_id}` and `PATCH .../photos/reorder` have all
// existed since W3. Only the DELETE had a caller — the photo block could show
// and remove photos but had no way to add any, so a user who had never uploaded
// straight into the block saw a permanent 「（尚未加入照片）」 and a whole API with
// no door.
//
// Two steps, because the API takes `album_item_ids` and an album can hold 58
// photos: pick the album, then pick the photos. The first step reuses
// `AlbumListRows` — the same rows the calendar's Q21 link picker uses, off the
// same `useAlbumList` cache key — rather than growing a second album list.
//
// 50 is the endpoint's own `max_length` on `album_item_ids`, so the counter here
// stops there and says why, instead of letting the request fail with a 422 the
// user cannot act on.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Check } from "lucide-react";

import { BottomSheet } from "@/components/patterns/BottomSheet";
import { LoadingState } from "@/components/primitives/LoadingState";
import { AlbumListRows } from "@/components/albums/AlbumListRows";
import { listAlbumItems, mediaFileUrl } from "@/lib/api/album";
import { attachPhotos, listPhotoItems } from "@/lib/api/wishBlocks";
import { Button } from "@/components/ui/button";

/** The endpoint's own cap — see `PhotoAttachRequest.album_item_ids`. */
const MAX_ATTACH = 50;

export interface WishPhotoPickerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wishId: string;
  blockId: string;
  busy?: boolean;
  onAttached: () => void;
  onError: (message: string) => void;
}

export function WishPhotoPickerSheet({
  open,
  onOpenChange,
  wishId,
  blockId,
  busy = false,
  onAttached,
  onError,
}: WishPhotoPickerSheetProps) {
  const [albumId, setAlbumId] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const items = useQuery({
    queryKey: ["album-items", albumId ?? "", "picker"],
    queryFn: () => listAlbumItems(albumId!, { limit: 200, offset: 0 }),
    enabled: open && albumId !== null,
  });

  // Already attached, so the grid can mark them instead of offering a tap that
  // would come back 409.
  const attached = useQuery({
    queryKey: ["wishes", "detail", wishId, "blocks", blockId, "photos"],
    queryFn: () => listPhotoItems(wishId, blockId),
    enabled: open,
  });
  const attachedIds = new Set((attached.data ?? []).map((p) => p.album_item_id));

  const reset = () => {
    setAlbumId(null);
    setChosen([]);
    setError(null);
  };

  const toggle = (id: string) => {
    setError(null);
    setChosen((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_ATTACH) {
        setError(`一次最多加入 ${MAX_ATTACH} 張照片。`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const submit = async () => {
    if (chosen.length === 0) return;
    setError(null);
    try {
      await attachPhotos(wishId, blockId, chosen);
      onAttached();
      onOpenChange(false);
      reset();
    } catch (err) {
      onError(err instanceof Error ? err.message : "加入照片失敗，請稍後再試。");
    }
  };

  const photos = (items.data?.items ?? []).filter((i) =>
    i.media.mime_type.startsWith("image/"),
  );

  return (
    <BottomSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      variant="mobile"
      className="max-h-[80vh] overflow-y-auto"
      title={albumId ? "挑選照片" : "從相簿加入照片"}
    >
      <div className="flex flex-col gap-4" data-testid="wish-photo-picker">
        {albumId === null ? (
          <AlbumListRows
            enabled={open}
            busy={busy}
            onlyWithPhotos
            testId="wish-photo-picker-albums"
            onPick={(id) => {
              setAlbumId(id);
              setChosen([]);
              setError(null);
            }}
          />
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setAlbumId(null);
                setChosen([]);
                setError(null);
              }}
              data-testid="wish-photo-picker-back"
              className="press flex min-h-[44px] w-fit items-center gap-1 text-body text-ink-2 hover:text-ink-1"
            >
              <ChevronLeft size={18} aria-hidden />
              換一本相簿
            </button>

            {items.isLoading ? (
              <LoadingState shape="row" count={3} />
            ) : photos.length === 0 ? (
              <p className="text-body-sm text-ink-2" data-testid="wish-photo-picker-empty">
                這本相簿沒有照片。
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-1" data-testid="wish-photo-picker-grid">
                {photos.map((item) => {
                  const isAttached = attachedIds.has(item.id);
                  const isChosen = chosen.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      disabled={isAttached || busy}
                      aria-pressed={isChosen}
                      data-testid={`wish-photo-option-${item.id}`}
                      onClick={() => toggle(item.id)}
                      className="press relative aspect-square overflow-hidden rounded-md bg-paper-deep disabled:opacity-40"
                    >
                      <img
                        src={mediaFileUrl(item.media.id)}
                        alt={item.caption ?? ""}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                      {isChosen ? (
                        <span
                          className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-you-strong text-paper"
                          data-testid="wish-photo-picked"
                        >
                          <Check size={14} aria-hidden />
                        </span>
                      ) : null}
                      {isAttached ? (
                        <span className="absolute bottom-0 left-0 right-0 bg-ink-1/75 px-1 py-0.5 text-center text-body-xs text-paper">
                          已加入
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}

            {error ? (
              <p className="text-body-sm text-partner-strong" role="alert" data-testid="wish-photo-picker-error">
                {error}
              </p>
            ) : null}

            <Button
              type="button"
              variant="primary"
              disabled={chosen.length === 0 || busy}
              onClick={() => void submit()}
              data-testid="wish-photo-picker-submit"
            >
              {chosen.length === 0
                ? "先挑選照片"
                : `加入 ${chosen.length} 張（上限 ${MAX_ATTACH}）`}
            </Button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
